import {
  createSerializer,
  parseAsString,
  parseAsStringEnum,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';
import {
  DEFAULT_IMAGE_PROMPT_CATEGORY,
  DEFAULT_IMAGE_PROMPT_MODEL,
  DEFAULT_IMAGE_PROMPT_SORT,
  IMAGE_PROMPT_CATEGORIES,
  IMAGE_PROMPT_MODEL_VALUES,
  IMAGE_PROMPT_SORT_VALUES,
  type ImagePromptCategory,
  type ImagePromptModel,
  type ImagePromptSort,
} from '@/lib/image-prompts';

const CATEGORY_VALUES = [
  DEFAULT_IMAGE_PROMPT_CATEGORY,
  ...IMAGE_PROMPT_CATEGORIES.map((c) => c.slug),
] as const satisfies readonly ImagePromptCategory[];

type LooseImagePromptSearch = Partial<
  Record<'model' | 'sort' | 'category' | 'q', unknown>
>;

export interface ImagePromptSearchState {
  model?: ImagePromptModel;
  sort?: ImagePromptSort;
  category?: ImagePromptCategory;
  q?: string;
}

function hasLiteralValue<const TValues extends readonly string[]>(
  values: TValues,
  value: unknown
): value is TValues[number] {
  return typeof value === 'string' && values.includes(value);
}

export function sanitizeImagePromptSearch(
  search?: LooseImagePromptSearch
): ImagePromptSearchState {
  const model = hasLiteralValue(IMAGE_PROMPT_MODEL_VALUES, search?.model)
    ? search.model
    : undefined;
  const sort = hasLiteralValue(IMAGE_PROMPT_SORT_VALUES, search?.sort)
    ? search.sort
    : undefined;
  const category = hasLiteralValue(CATEGORY_VALUES, search?.category)
    ? search.category
    : undefined;
  const q = typeof search?.q === 'string' ? search.q.trim() : '';

  return {
    model: model === DEFAULT_IMAGE_PROMPT_MODEL ? undefined : model,
    sort: sort === DEFAULT_IMAGE_PROMPT_SORT ? undefined : sort,
    category: category === DEFAULT_IMAGE_PROMPT_CATEGORY ? undefined : category,
    q: q || undefined,
  };
}

export function mergeImagePromptSearch(
  search: LooseImagePromptSearch | undefined,
  overrides: Partial<ImagePromptSearchState>
): ImagePromptSearchState {
  return sanitizeImagePromptSearch({
    ...sanitizeImagePromptSearch(search),
    ...overrides,
  });
}

/**
 * nuqs parsers for the home / gallery search params. Defaults are chosen
 * so the canonical `/` URL implies "all + all + newest"; nuqs strips
 * any value equal to its default from the URL automatically.
 */
export const imagePromptSearchParsers = {
  model: parseAsStringEnum<ImagePromptModel>([
    ...IMAGE_PROMPT_MODEL_VALUES,
  ]).withDefault(DEFAULT_IMAGE_PROMPT_MODEL),
  sort: parseAsStringEnum<ImagePromptSort>([
    ...IMAGE_PROMPT_SORT_VALUES,
  ]).withDefault(DEFAULT_IMAGE_PROMPT_SORT),
  category: parseAsStringLiteral(CATEGORY_VALUES).withDefault(
    DEFAULT_IMAGE_PROMPT_CATEGORY
  ),
  q: parseAsString.withDefault(''),
} as const;

/**
 * Hook used by the gallery filter bar. `history: 'replace'` keeps the
 * back button useful (one entry per visit, not one per filter click).
 */
export function useImagePromptSearch() {
  return useQueryStates(imagePromptSearchParsers, { history: 'replace' });
}

/**
 * Build a canonical gallery URL with the given filter overrides.
 * Defaults are stripped automatically:
 *   serializeImagePromptSearch({ category: 'fashion' }) // "?category=fashion"
 *   serializeImagePromptSearch({ category: 'all' })     // ""
 */
export const serializeImagePromptSearch = createSerializer(
  imagePromptSearchParsers
);
